import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import RestaurantCard from "@/components/RestaurantCard";
import { Heart, ChevronRight } from "lucide-react";

export default function Favorites() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/favorites");
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (user) load();
    else setLoading(false);
    return () => { cancelled = true; };
  }, [user]);

  return (
    <div className="max-w-md mx-auto px-5 pt-6 pb-28 bg-[#FAFAFA] min-h-screen" data-testid="favorites-page">
      <div className="flex items-center gap-2 mb-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full bg-white border border-[#EEE] flex items-center justify-center"
          data-testid="favorites-back"
        >
          <ChevronRight className={`w-5 h-5 text-[#1A1A1A] ${lang === "ar" ? "" : "rotate-180"}`} />
        </button>
        <h1 className="font-display text-2xl font-black text-[#1A1A1A]">
          {lang === "ar" ? "المفضلة" : "Favorites"}
        </h1>
      </div>

      {loading ? (
        <div className="space-y-3" data-testid="favorites-loading">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-3 border border-[#EAEAEA] animate-pulse h-[132px]" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16" data-testid="favorites-empty">
          <div className="inline-flex w-24 h-24 rounded-full bg-[#FEF0EE] items-center justify-center mb-5">
            <Heart className="w-11 h-11 text-[#E05D3A]" strokeWidth={1.8} />
          </div>
          <h2 className="font-display text-xl font-black text-[#1A1A1A] mb-2">
            {lang === "ar" ? "لا توجد مفضلة بعد" : "No favorites yet"}
          </h2>
          <p className="text-[#6B6B6B] text-sm mb-6 max-w-xs mx-auto">
            {lang === "ar"
              ? "اضغط على القلب ❤ في أي متجر لإضافته إلى مفضلتك"
              : "Tap the heart on any store to save it here"}
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white px-8 py-3 font-bold"
            data-testid="favorites-browse-cta"
          >
            {lang === "ar" ? "تصفّح المتاجر" : "Browse stores"}
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="favorites-list">
          {items.map((r) => <RestaurantCard key={r.id} r={r} />)}
        </div>
      )}
    </div>
  );
}
